import React, { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    YooMoneyCheckoutWidget: new (options: {
      confirmation_token: string;
      return_url?: string;
      customization?: {
        colors?: {
          control_primary?: string;
          background?: string;
        };
      };
      error_callback?: (error: unknown) => void;
    }) => {
      render: (containerId: string) => void;
      destroy: () => void;
    };
  }
}

interface YooKassaWidgetProps {
  confirmation_token: string;
  return_url?: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

const SCRIPT_ID = 'yookassa-checkout-widget';
const SCRIPT_SRC = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';

const YooKassaWidget: React.FC<YooKassaWidgetProps> = ({
  confirmation_token,
  return_url,
  onSuccess,
  onError,
}) => {
  const containerId = useRef(`yookassa-widget-${Math.random().toString(36).slice(2, 9)}`);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const widgetRef = useRef<ReturnType<() => { destroy: () => void }> | null>(null);

  // Загрузка скрипта виджета
  useEffect(() => {
    if (document.getElementById(SCRIPT_ID)) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      setError('Не удалось загрузить виджет оплаты');
      onError?.('Script load error');
    };
    document.head.appendChild(script);

    return () => {
      // Не удаляем скрипт — он может использоваться другими виджетами
    };
  }, [onError]);

  // Инициализация виджета после загрузки скрипта
  useEffect(() => {
    if (!scriptLoaded || !confirmation_token) return;

    if (!window.YooMoneyCheckoutWidget) {
      setError('Виджет оплаты недоступен');
      onError?.('Widget not available');
      return;
    }

    try {
      const checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token,
        return_url: return_url || `${window.location.origin}/subscription?payment=success`,
        customization: {
          colors: {
            control_primary: '#d4af37', // Золотой акцент в стиле сайта
            background: '#0d1428', // Тёмный фон в стиле сайта
          },
        },
        error_callback: (err) => {
          console.error('[YooKassa Widget] Error:', err);
          setError('Ошибка инициализации виджета');
          onError?.(err);
        },
      });

      widgetRef.current = checkout;
      checkout.render(containerId.current);

      // Отслеживание успешной оплаты через возврат на return_url
      // Виджет автоматически редиректит после успеха
    } catch (err) {
      console.error('[YooKassa Widget] Init error:', err);
      setError('Ошибка инициализации виджета');
      onError?.(err);
    }

    return () => {
      if (widgetRef.current) {
        widgetRef.current.destroy();
        widgetRef.current = null;
      }
    };
  }, [scriptLoaded, confirmation_token, return_url, onError, onSuccess]);

  if (error) {
    return (
      <div style={{
        background: 'rgba(220, 53, 69, 0.2)',
        border: '1px solid #dc3545',
        borderRadius: '12px',
        padding: '16px',
        color: '#dc3545',
        textAlign: 'center',
        fontSize: '14px',
      }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      <div id={containerId.current} style={{ minHeight: '300px' }} />
      {!scriptLoaded && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#b0b0b0',
          fontSize: '14px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(212, 175, 55, 0.2)',
            borderTopColor: '#d4af37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          Загрузка виджета оплаты...
        </div>
      )}
    </div>
  );
};

export default YooKassaWidget;
